import * as React from "react";
import { Plus, Trash2, RefreshCcw } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";

/**
 * RoutineGoalView — detail body for a legacy "routine" goal.
 *
 * NOTE: `routine` is being removed as a goal type in a later PR (Workstream
 * D.2), which migrates existing routine-type goals to standalone routines and
 * moves this surface into RoutinesPage. It is preserved here (extracted verbatim
 * as ProcessTrialsSection) so this decomposition PR is a pure refactor with no
 * behavior change for existing routine-type goals.
 */
export function RoutineGoalView({
  goalId,
  goalStatus,
  isOwner,
}: {
  goalId: string;
  goalStatus: string;
  isOwner: boolean;
}) {
  return <ProcessTrialsSection goalId={goalId} goalStatus={goalStatus} isOwner={isOwner} />;
}

interface ProcessFlow {
  id: string;
  title: string;
  description: string | null;
  frequency: string;
  estimatedMinutes: number | null;
  tasks: { id: string; label: string; isCompleted: boolean }[];
  lastCompletedAt: string | null;
  lastFeedback: string | null;
  createdAt: string;
}

function ProcessTrialsSection({ goalId, goalStatus, isOwner }: { goalId: string; goalStatus: string; isOwner: boolean }) {
  const toast = useToast();
  const [flows, setFlows] = React.useState<ProcessFlow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState("");
  const [newDescription, setNewDescription] = React.useState("");
  const [newFrequency, setNewFrequency] = React.useState("weekly");
  const [newMinutes, setNewMinutes] = React.useState("");
  const [newTasks, setNewTasks] = React.useState<string[]>([""]);

  const fetchFlows = React.useCallback(async () => {
    try {
      const data = await apiFetch<ProcessFlow[]>(`/api/organizing-goals/${goalId}/process-trials`);
      setFlows(data);
    } catch {
      // Legacy surface (routine-type goals are no longer creatable); a load failure
      // just leaves the list empty — surface it quietly rather than silently.
      toast.show("Couldn't load routines for this goal.", "error");
    }
    setLoading(false);
  }, [goalId, toast]);

  React.useEffect(() => { fetchFlows(); }, [fetchFlows]);

  const handleCreate = async () => {
    const tasks = newTasks
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
      .map((label, idx) => ({ id: `task-${idx}`, label, isCompleted: false }));
    if (!newTitle.trim() || tasks.length === 0) return;
    setCreating(true);
    try {
      await apiFetch(`/api/organizing-goals/${goalId}/process-trials`, {
        method: "POST",
        body: JSON.stringify({
          title: newTitle.trim(),
          description: newDescription.trim() || null,
          frequency: newFrequency,
          estimatedMinutes: newMinutes ? parseInt(newMinutes) : null,
          tasks,
        }),
      });
      toast.show("Routine created! It'll appear in your workflows.", "success");
      setShowCreate(false);
      setNewTitle("");
      setNewDescription("");
      setNewFrequency("weekly");
      setNewMinutes("");
      setNewTasks([""]);
      await fetchFlows();
    } catch (err: any) {
      toast.show(err.message || "Failed to create routine", "error");
    }
    setCreating(false);
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg border border-clay-border p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-[1.0625rem] text-ink flex items-center gap-2">
          <RefreshCcw className="w-4 h-4 text-faint" />Process trials (routines)
        </h2>
        {isOwner && goalStatus === "active" && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 text-sm text-primary font-medium hover:text-amethyst-ink py-2"
          >
            <Plus className="w-3.5 h-3.5" />Add routine
          </button>
        )}
      </div>

      {flows.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-body mb-2">
            Not just about placement — try a new <em>process</em> to support this goal.
          </p>
          <p className="text-[0.8125rem] text-faint">
            e.g., "5-min evening reset" or "Weekly lunch prep"
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {flows.map((flow) => (
            <div key={flow.id} className="p-3 rounded-md border border-clay-border hover:bg-amethyst-soft">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink">{flow.title}</span>
                  <span className="text-[0.8125rem] px-2 py-0.5 rounded-pill bg-amethyst-soft text-amethyst-ink font-medium">
                    {flow.frequency}
                  </span>
                </div>
                {flow.estimatedMinutes && (
                  <span className="text-[0.8125rem] text-faint tabular-nums">{flow.estimatedMinutes} min</span>
                )}
              </div>
              {flow.description && (
                <p className="text-[0.8125rem] text-body mt-1">{flow.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-[0.8125rem] text-faint">
                <span className="tabular-nums">{flow.tasks.length} step{flow.tasks.length !== 1 ? "s" : ""}</span>
                {flow.lastCompletedAt && (
                  <span className="tabular-nums">Last done: {new Date(flow.lastCompletedAt).toLocaleDateString()}</span>
                )}
                {flow.lastFeedback && (
                  <span className={flow.lastFeedback === "easy" ? "text-sage-deep" : flow.lastFeedback === "too_much" ? "text-warning-text" : "text-faint"}>
                    Feedback: {flow.lastFeedback === "too_much" ? "too much" : flow.lastFeedback}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create routine modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add a routine to this goal">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Routine name</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g., '5-min evening lunchbox reset'"
              className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              maxLength={200}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-body mb-1 block">Description (optional)</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What does this routine accomplish?"
              className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-body mb-1 block">Frequency</label>
              <select
                value={newFrequency}
                onChange={(e) => setNewFrequency(e.target.value)}
                className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-body mb-1 block">Est. minutes</label>
              <input
                type="number"
                value={newMinutes}
                onChange={(e) => setNewMinutes(e.target.value)}
                placeholder="5"
                min={1}
                max={480}
                className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-body mb-2 block">Steps</label>
            <div className="space-y-2">
              {newTasks.map((task, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-[0.8125rem] text-faint w-5 text-center tabular-nums">{idx + 1}.</span>
                  <input
                    type="text"
                    value={task}
                    onChange={(e) => {
                      const updated = [...newTasks];
                      updated[idx] = e.target.value;
                      setNewTasks(updated);
                    }}
                    placeholder="What to do..."
                    className="flex-1 px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                  {newTasks.length > 1 && (
                    <button
                      onClick={() => setNewTasks(newTasks.filter((_, i) => i !== idx))}
                      aria-label={`Remove step ${idx + 1}`}
                      className="text-faint hover:text-error p-2.5 rounded-md"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {newTasks.length < 20 && (
                <button
                  onClick={() => setNewTasks([...newTasks, ""])}
                  className="text-sm text-primary font-medium hover:text-amethyst-ink flex items-center gap-1 ml-7 py-2"
                >
                  <Plus className="w-3 h-3" />Add step
                </button>
              )}
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={!newTitle.trim() || newTasks.filter((t) => t.trim()).length === 0 || creating}
            className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            {creating ? "Creating..." : "Create routine"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
